// src/components/report/SavingsJourney.js
import React from 'react';

const SavingPatternCard = ({ label, value, subtitle }) => (
    <div>
        <div className="text-sm text-gray-600">{label}</div>
        <div className="text-xl font-bold text-[#234363] ltr">
            {value}
        </div>
        {subtitle && (
            <div className="text-sm text-gray-600 mt-1">
                {subtitle}
            </div>
        )}
    </div>
);

const NextLevelCard = ({ currentLevel, nextLevel, monthlyTarget, monthlyGold, yearlyGold }) => (
    <div className="bg-gray-50 p-4 rounded-lg">
        <div className="text-sm font-medium text-[#234363] mb-2">
            {nextLevel ? `المستوى القادم: ${nextLevel}` : 'أنت في المستوى الاستثنائي'}
        </div>
        {nextLevel ? (
            <>
                <div className="text-sm text-gray-600 mb-2">
                    امكانيات المستوى القادم ({monthlyTarget.toLocaleString()} جنيه شهرياً):
                </div>
                <div className="space-y-2 text-gray-600 text-sm">
                    <div>• {monthlyGold.toFixed(3)} جرام دهب شهرياً</div>
                    <div>• {yearlyGold.toFixed(3)} جرام دهب سنوياً</div>
                </div>
            </>
        ) : (
            <div className="text-sm text-gray-600">
                حافظ على معدل التحويش مع زيادة سنوية تواكب التضخم
            </div>
        )}
    </div>
);

const SavingsJourney = ({ reportData }) => {
    const monthlyAverage = reportData?.performance_metrics?.investment_summary?.average_monthly_investment || 0;
    const currentPrice = reportData?.purchase_metrics?.current_gold_price_per_gram || 4440;
    
    // Calculate savings patterns
    const dailyAmount = (monthlyAverage / 30).toFixed(0);
    const monthlyGold = (monthlyAverage / currentPrice).toFixed(3);
    const yearlyGold = (monthlyGold * 12).toFixed(3);
    
    // Determine saving level
    const getSavingLevel = (amount) => {
        if (amount < 1000) return { 
            level: 'promising', 
            title: 'مستكشف الدهب',
            message: 'كل جنيه متحوش خطوة في طريق النجاح... الأهم الاستمرار والانتظام'
        };
        if (amount < 2000) return { 
            level: 'rising', 
            title: 'أستاذ الدهب',
            message: 'تخطيط ذكي لمستقبل دهبي... ثبات الخطوات هو سر النجاح'
        };
        if (amount < 5000) return { 
            level: 'established', 
            title: 'حكيم الدهب',
            message: 'خبرة في التحويش بتتكلم عن نفسها... استثمار بيعكس فهم قيمة الدهب'
        };
        if (amount < 10000) return { 
            level: 'advanced', 
            title: 'كينج الدهب',
            message: 'احتراف في التحويش بيظهر كل شهر... مستوى عالي يشجع الكل'
        };
        return { 
            level: 'exceptional', 
            title: 'أسطورة الدهب',
            message: 'مستوى استثنائي يجعلك قدوة في التحويش الدهبي'
        };
    };

    const getNextLevel = (currentAmount) => {
        if (currentAmount < 1000) return { title: 'أستاذ الدهب', target: 2000 };
        if (currentAmount < 2000) return { title: 'حكيم الدهب', target: 5000 };
        if (currentAmount < 5000) return { title: 'سلطان الدهب', target: 10000 };
        if (currentAmount < 10000) return { title: 'أسطورة الدهب', target: 15000 };
        return null;
    };

    const currentSavingLevel = getSavingLevel(monthlyAverage);
    const nextLevel = getNextLevel(monthlyAverage);
    
    return (
        <div className="space-y-6">
            {/* Current Saving Pattern */}
            <div className="bg-white rounded-xl p-6 shadow-lg">
            <h3 className="text-lg font-bold text-[#234363] mb-2">
    أنت {currentSavingLevel.title}
</h3>
<p className="text-sm text-gray-600 mb-6">
    {currentSavingLevel.message}
</p>
                
                {/* Saving Pattern Details */}
<div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
    <SavingPatternCard
        label="معدل التحويش اليومي"
        value={`EGP ${Number(dailyAmount).toLocaleString()}`}
    />
    <SavingPatternCard
        label="معدل التحويش الشهري"
        value={`EGP ${monthlyAverage.toLocaleString()}`}
    />
    <SavingPatternCard
        label="معدل التحويش في الدهب"
        value={`${monthlyGold} g/شهر`}
        subtitle={`${yearlyGold} g/سنة`}
    />
    <SavingPatternCard
        label="أفضل شهر"
        value={`EGP ${Number(reportData?.performance_metrics?.investment_summary?.best_month || 0).toLocaleString()}`}
    />
    <SavingPatternCard
        label="شهور متتالية"
        value={reportData?.performance_metrics?.consistency_score?.max_consecutive_months || 0}
        subtitle="شهر"
    />
    <SavingPatternCard
        label="معدل الانتظام"
        value={`${reportData?.performance_metrics?.investment_summary?.investment_frequency || 0}%`}
    />
</div>

                {/* Growth Opportunities Section */}
                <div className="border-t pt-4">
                    <h4 className="font-medium text-[#234363] mb-4">فرصة تزود تحويشتك</h4>
                    <NextLevelCard
                        currentLevel={currentSavingLevel.title}
                        nextLevel={nextLevel?.title}
                        monthlyTarget={nextLevel?.target || monthlyAverage}
                        monthlyGold={(nextLevel?.target || monthlyAverage) / currentPrice}
                        yearlyGold={((nextLevel?.target || monthlyAverage) * 12) / currentPrice}
                    />
                </div>
            </div>
        </div>
    );
};

export default SavingsJourney;